// core/srs/answer-match.js — TA·1: pure answer matching (never model-based). Typing the answer
// beats recognize-then-flip for short factual backs and makes "I knew it" self-deception
// impossible. Returns a verdict: "exact" | "close" | "miss".
//
// Tiers: normalize (case, punctuation, leading articles, whitespace) → exact match; else a
// forgiving CLOSE tier — a single-edit typo (Levenshtein ≤ 1 on normalized, for non-trivial
// answers) OR a contained-answer match for short backs (the typed answer contains, or is
// contained by, the expected — "mitochondria" vs "the mitochondria"). Everything else is a miss.

const ARTICLES = /^(the|a|an)\s+/i;

/** Normalize an answer for comparison: lowercase, strip punctuation, drop a leading article, collapse space. */
export function normalizeAnswer(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // fold accents (café → cafe)
    .replace(/[^\p{L}\p{N}\s]/gu, " ")                 // punctuation → space
    .replace(/\s+/g, " ")
    .trim()
    .replace(ARTICLES, "");
}

/** Levenshtein edit distance (iterative, two-row). */
export function editDistance(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  let curr = new Array(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/**
 * Grade a typed answer against the expected back.
 * @param {string} typed
 * @param {string} expected
 * @returns {{ verdict: "exact"|"close"|"miss", normalizedTyped: string, normalizedExpected: string }}
 */
export function matchAnswer(typed, expected) {
  const nt = normalizeAnswer(typed);
  const ne = normalizeAnswer(expected);
  let verdict = "miss";
  if (nt && nt === ne) {
    verdict = "exact";
  } else if (nt && ne) {
    // a single typo counts as close, but only once the answer is long enough that one edit isn't
    // the whole word (so "cat" vs "bat" stays a miss, "mitochondria" vs "mitochondia" is close)
    const dist = editDistance(nt, ne);
    if (dist <= 1 && Math.min(nt.length, ne.length) >= 4) {
      verdict = "close";
    } else if ((ne.length <= 24 || nt.length <= 24) && (ne.includes(nt) || nt.includes(ne)) && Math.min(nt.length, ne.length) >= 3) {
      // contained-answer for short backs: "occipital lobe" vs "occipital", "V3" is too short → guarded by ≥3
      verdict = "close";
    }
  }
  return { verdict, normalizedTyped: nt, normalizedExpected: ne };
}

/** The FSRS grade a verdict SUGGESTS (always overridable by the kid). */
export const VERDICT_GRADE = Object.freeze({ exact: "good", close: "hard", miss: "again" });
